import { type FC } from "react";
import { Card } from "@snapchat/snap-design-system";
import { generatePath } from "react-router-dom";
import { AbsoluteRoute } from "../../../../core/routes";
import { sectionNavigationContainer, LinkBack, UnsavedChangesPrompt, Visible } from "../../../common/components";
import { VendorDataCollection } from "../../../vendor-data/models";
import { type VendorDataChangedAction } from "../../../vendor-data";
import { GlobalLensDetailsModel, LensModel } from "../models";
import { LensInfoSection } from "./LensInfoSection";
import { DetailsSkeletonContainer } from "../../../dashboard/components/DetailsSkeletonContainer";
import { type AddLensGroupInfo, AddToLensGroupModal } from "../../../groups/components/add-to-lensgroup-modal";
import { CreateGroupContainerV2 } from "../../../groups/containers";
import { useModal } from "../../../core/hooks";
import { GroupInfoModel } from "../../../groups/model";
import { RangeModifier } from "../../../connections/models";
import { type Permissions } from "../../../gql-generated/aliases";
import { NoResults } from "../../../groups/components";
import { GroupInfoSection } from "./GroupInfoSection";

export interface GlobalLensDetailsProps {
    lens: LensModel;
    group: GroupInfoModel;
    isError: boolean;
    loading: boolean;
    lensGroups: GlobalLensDetailsModel;
    saveInProgress: boolean;
    organizationId: string;
    globalVendorData: VendorDataCollection;
    onSave: () => void;
    onScheduleChanged: (scheduleRange: RangeModifier, groupId: string) => void;
    onAddLensToGroups: (groups: AddLensGroupInfo[]) => void;
    onRemoveFromGroup: (groupId: string) => void;
    onGlobalVendorDataChanged: (event: VendorDataChangedAction) => void;
    onLensInGroupVendorDataChanged: (event: VendorDataChangedAction, groupId: string) => void;
    permissions: Permissions;
}

export const GlobalLensDetails: FC<GlobalLensDetailsProps> = ({
    lens,
    group,
    loading,
    organizationId,
    lensGroups,
    saveInProgress,
    globalVendorData,
    onSave,
    onScheduleChanged,
    onAddLensToGroups,
    onRemoveFromGroup,
    onGlobalVendorDataChanged,
    onLensInGroupVendorDataChanged,
    isError,
    permissions,
}) => {
    const [addToGroupModalProps, showAddToGroupModal] = useModal();

    return (
        <DetailsSkeletonContainer loading={loading}>
            <div className={sectionNavigationContainer}>
                <Visible when={group?.id.length > 0}>
                    <LinkBack
                        to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_GROUP, {
                            orgId: organizationId,
                            groupId: group?.id ?? "",
                        })}
                    >
                        &lt; Back to {group.name}
                    </LinkBack>
                </Visible>
                <Visible when={group?.id.length === 0}>
                    <LinkBack
                        to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_LENSES, {
                            orgId: organizationId,
                        })}
                    >
                        &lt; Back to All Lenses
                    </LinkBack>
                </Visible>
                <Visible when={isError}>
                    <Card titleBorder={false}>
                        <NoResults description="Lens not found" />
                    </Card>
                </Visible>
                <Visible when={!isError}>
                    <LensInfoSection
                        saveIsDisabled={
                            loading ||
                            !lensGroups.isValid ||
                            !globalVendorData.isCollectionValid ||
                            (!globalVendorData.hasChanged && !lensGroups.hasChanged)
                        }
                        saveInProgress={saveInProgress}
                        onSave={onSave}
                        globalVendorData={globalVendorData}
                        onGlobalVendorDataChanged={onGlobalVendorDataChanged}
                        lens={lens}
                        onShowAddToGroupModal={showAddToGroupModal}
                        permissions={permissions}
                    />
                    {lensGroups.visibleGroups.map(
                        (
                            { name, priority, vendorData: vendorDataItems, groupId, scheduleRange, numberOfLenses },
                            index,
                        ) => (
                            <GroupInfoSection
                                key={index}
                                name={name}
                                groupId={groupId}
                                organizationId={organizationId}
                                priority={priority}
                                vendorDataItems={vendorDataItems}
                                scheduleRange={scheduleRange}
                                numberOfLenses={numberOfLenses}
                                onScheduleChanged={onScheduleChanged}
                                onLensInGroupVendorDataChanged={onLensInGroupVendorDataChanged}
                                onRemoveFromGroup={onRemoveFromGroup}
                                permissions={permissions}
                                lens={lens}
                            />
                        ),
                    )}
                    <CreateGroupContainerV2>
                        {({ createGroup, groupIsCreated, newGroups }) => (
                            <AddToLensGroupModal
                                {...addToGroupModalProps}
                                onAddToGroup={onAddLensToGroups}
                                onCreateGroup={createGroup}
                                groupIsCreated={groupIsCreated}
                                alreadySelectedGroupIds={lensGroups.groupIds}
                                newGroups={newGroups}
                            />
                        )}
                    </CreateGroupContainerV2>
                    <UnsavedChangesPrompt when={lensGroups.hasChanged || globalVendorData.hasChanged} />
                </Visible>
            </div>
        </DetailsSkeletonContainer>
    );
};
